/**
 * Junto Frontend ReservationPopup Component
 * 
 * Popup for reserving/booking a class with payment method selection.
 * Theme-agnostic and reusable across different studio brands.
 */

import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { solRoomTheme } from '../../themes/sol-room.js';
import { useAuth } from '../../contexts/ClerkAuthContext';
import BookingPopupBase, {
  ClassInfoSection,
  NoPaymentOptionsSection,
  ButtonContainer,
  ActionButton,
  usePaymentValidation,
  findMinCostCreditPackage,
  isClassFree
} from './BookingPopupBase';
import IsolatedPurchasePopup from '../CartSystem/IsolatedPurchasePopup';

const PaymentSection = styled.div`
  margin-bottom: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

const SectionTitle = styled.h5`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md} 0;
`;

const PaymentOption = styled.div`
  background: ${props => props.$selected ? (props.theme?.colors?.accent?.bright || solRoomTheme.colors.accent.bright) : (props.theme?.colors?.neutral?.lightest || solRoomTheme.colors.neutral.lightest)};
  border: 2px solid ${props => props.$selected ? (props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main) : (props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light)};
  border-radius: 8px;
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  margin-bottom: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  cursor: pointer;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  
  &:hover {
    border-color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
    background: ${props => props.theme?.colors?.accent?.bright || solRoomTheme.colors.accent.bright};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PaymentTitle = styled.div`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin-bottom: ${props => props.theme?.spacing?.xs || solRoomTheme.spacing.xs};
`;

const PaymentDescription = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
`;

const ReservationPopup = ({ 
  isOpen, 
  onClose, 
  classData, 
  onConfirm,
  theme = solRoomTheme 
}) => {
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [purchasePopupOpen, setPurchasePopupOpen] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const { accountData } = useAuth();
  usePaymentValidation(classData);

  // Check if class is free (scenario 4)
  const isFreeClass = isClassFree(classData);

  // Get available payment options based on user's account
  const getPaymentOptions = () => {
    const options = [];
    
    // 1. Check for unlimited membership
    if (accountData?.memberships?.find(m => 
      m.status === 'active' && m.membership_type === 'unlimited'
    )) {
      options.push({
        id: 'membership',
        title: 'Unlimited Membership',
        description: 'Use your unlimited membership'
      });
    }

    // Check for limited memberships with remaining classes
    const limitedMembership = accountData?.memberships?.find(m => 
      m.status === 'active' && m.classes_remaining > 0
    );
    if (limitedMembership) {
      options.push({
        id: 'membership',
        title: `${limitedMembership.membership_type} Membership`,
        description: `${limitedMembership.classes_remaining} classes remaining`
      });
    }
    
    // 2. Check for usable credits
    if (accountData?.wallet?.credits?.length > 0) {
      const classCategory = classData?.category?.id || classData?.class_category_id;
      
      const usableCredits = accountData.wallet.credits.filter(credit => {
        if (credit.expiry_date && new Date(credit.expiry_date) < new Date()) return false;
        if (credit.remaining_uses <= 0) return false;
        
        if (classCategory && credit.class_categories) {
          return credit.class_categories.includes(classCategory);
        }
        
        return true;
      });

      usableCredits.forEach(credit => {
        options.push({
          id: 'credit',
          creditId: credit.id,
          title: credit.credit_type?.name || 'Class Credit',
          description: `${credit.remaining_uses} remaining`
        });
      });
    }
    
    return options;
  };

  const paymentOptions = getPaymentOptions();
  const userHasExistingOptions = paymentOptions.length > 0;

  // Auto-select the first available option
  useEffect(() => {
    if (paymentOptions.length > 0 && !selectedPayment) {
      setSelectedPayment(paymentOptions[0].id);
    }
  }, [paymentOptions, selectedPayment]);

  // Reset when popup opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedPayment(null);
      setPendingBooking(null);
    }
  }, [isOpen]);

  // Handle direct purchase (scenario 3)
  const handleDirectPurchase = async () => {
    try {
      const minCostPackage = await findMinCostCreditPackage(classData);
      
      if (!minCostPackage) {
        console.error('No applicable credit package found for this class');
        return;
      }

      // Store the booking for after purchase
      setPendingBooking({
        classData,
        paymentMethod: 'purchased_credit',
        creditPackage: minCostPackage
      });

      // Close reservation popup and open purchase popup
      onClose();
      setPurchasePopupOpen(true);
      
    } catch (error) {
      console.error('Failed to initiate direct purchase:', error);
    }
  };

  // Handle purchase completion and auto-booking
  const handlePurchaseComplete = () => {
    setPurchasePopupOpen(false);
    
    if (pendingBooking && onConfirm) {
      // Automatically book the class using the purchased credit
      onConfirm(pendingBooking.classData, pendingBooking.paymentMethod, {
        creditPackage: pendingBooking.creditPackage
      });
    }
    
    setPendingBooking(null);
  };

  const handleConfirm = () => {
    if (selectedPayment && onConfirm) {
      onConfirm(classData, selectedPayment);
    }
    onClose();
  };

  if (!classData) return null;

  return (
    <>
      <BookingPopupBase 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Reserve Class"
        theme={theme}
      >
        <ClassInfoSection classData={classData} theme={theme} />

        {/* Scenario 4: Free class - direct booking */}
        {isFreeClass ? (
          <ButtonContainer theme={theme}>
            <ActionButton onClick={onClose} theme={theme}>
              Cancel
            </ActionButton>
            <ActionButton $primary onClick={handleConfirm} theme={theme}>
              Reserve (Free)
            </ActionButton>
          </ButtonContainer>
        ) : userHasExistingOptions ? (
          /* Scenarios 1 & 2: User has membership or credits */
          <>
            <PaymentSection theme={theme}>
              <SectionTitle theme={theme}>Payment Method</SectionTitle>
              {paymentOptions.map((option, index) => (
                <PaymentOption
                  key={`${option.id}-${index}`}
                  $selected={selectedPayment === option.id}
                  onClick={() => setSelectedPayment(option.id)}
                  theme={theme}
                >
                  <PaymentTitle theme={theme}>{option.title}</PaymentTitle>
                  <PaymentDescription theme={theme}>{option.description}</PaymentDescription>
                </PaymentOption>
              ))}
            </PaymentSection>

            <ButtonContainer theme={theme}>
              <ActionButton onClick={onClose} theme={theme}>
                Cancel
              </ActionButton>
              <ActionButton 
                $primary 
                onClick={handleConfirm} 
                disabled={!selectedPayment}
                theme={theme}
              >
                Reserve Class
              </ActionButton>
            </ButtonContainer>
          </>
        ) : (
          /* Scenario 3: No existing options - show direct purchase */
          <NoPaymentOptionsSection 
            onClose={onClose} 
            onDirectPurchase={handleDirectPurchase}
            theme={theme} 
          />
        )}
      </BookingPopupBase>

      {/* Isolated purchase popup for direct purchases */}
      <IsolatedPurchasePopup 
        isOpen={purchasePopupOpen}
        onClose={() => {
          setPurchasePopupOpen(false);
          setPendingBooking(null);
        }}
        onComplete={handlePurchaseComplete}
        item={pendingBooking ? {
          id: `credit_${pendingBooking.creditPackage.name}`,
          name: pendingBooking.creditPackage.name,
          price: pendingBooking.creditPackage.price || pendingBooking.creditPackage.package_price,
          quantity: 1,
          itemType: 'credit',
          category: 'group',
          originalData: pendingBooking.creditPackage
        } : null}
        title="Purchase Credit + Reserve Class"
        description="This credit purchase will automatically reserve your class upon completion."
        theme={theme}
      />
    </>
  );
};

export default ReservationPopup;